import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/lib/utils";
import type { GenieLoaderProps } from "@/types/ui/genie-loader";

/**
 * GenieLoader — the single, standardised Help Genie loading state.
 *
 * THE Help Genie is summoned out of the lamp smoke while we work. Three sizes:
 *   - `fullscreen` — fixed, opaque overlay for whole-route / whole-page loads
 *   - `block`      — medium, centred loader for in-page content regions (default)
 *   - `inline`     — small, single bobbing Genie for tight spots (optional text)
 *
 * This replaces the old pulsing-logo loader, the generic spinner overlay, and
 * the bespoke genie-creation loader. The loader always shows ONE generic Help
 * Genie — never the industry mascots that hold props (a toaster, a wrench, …),
 * which read as random and off-brand in a loading state. The art is a
 * transparent, light-safe cutout of the canonical Genie so it composites on the
 * light loader surface.
 */

const BRAND = "#4E9CFF";
const SECONDARY = "#FF4DB8";
const GRADIENT = `linear-gradient(90deg, ${BRAND} 0%, ${SECONDARY} 100%)`;

/** The one generic Help Genie shown in every loading state. */
const LOADING_GENIE = "/genie-mascots/loading-genie.png";

const DEFAULT_MESSAGES = [
  "Summoning your Genie…",
  "Polishing the lamp…",
  "Gathering the magic…",
  "Almost there…",
];

/** Mascot height per size. */
const HEIGHT: Record<NonNullable<GenieLoaderProps["size"]>, number> = {
  inline: 46,
  block: 200,
  fullscreen: 420,
};

/* ── atmosphere ─────────────────────────────────────────────────────────── */

/** Smoke billowing up from the lamp at the base — the mascot reads as conjured. */
const RisingSmoke = ({ scale = 1 }: { scale?: number }) => {
  const puffs = [
    { x: -34, size: 150, color: BRAND, peak: 0.45, dur: 5, delay: 0 },
    { x: 30, size: 170, color: SECONDARY, peak: 0.4, dur: 5.6, delay: 0.7 },
    { x: -6, size: 200, color: "#FFFFFF", peak: 0.5, dur: 4.6, delay: 0.3 },
    { x: 48, size: 140, color: BRAND, peak: 0.38, dur: 6, delay: 1.3 },
    { x: -52, size: 150, color: SECONDARY, peak: 0.36, dur: 5.4, delay: 1 },
  ];
  return (
    <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-[62%] overflow-hidden">
      {puffs.map((p, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0 }}
          animate={{
            opacity: [0, p.peak, 0],
            y: [40 * scale, -120 * scale],
            scale: [0.6 * scale, 1.5 * scale],
            x: [p.x * 0.4 * scale, p.x * scale],
          }}
          transition={{ duration: p.dur, delay: p.delay, repeat: Infinity, ease: "easeOut" }}
          className="absolute left-1/2 bottom-0 rounded-full"
          style={{
            width: p.size * scale,
            height: p.size * scale,
            marginLeft: (-p.size * scale) / 2,
            background: `radial-gradient(circle, ${p.color}66 0%, ${p.color}22 42%, transparent 70%)`,
            filter: `blur(${28 * scale}px)`,
          }}
        />
      ))}
    </div>
  );
};

/** Soft brand halo behind the mascot — blurred, never a solid blob. */
const Halo = ({ size }: { size: number }) => (
  <motion.div
    aria-hidden
    className="absolute rounded-full"
    style={{
      width: size,
      height: size,
      background: `radial-gradient(circle, ${BRAND}33 0%, ${SECONDARY}1f 45%, transparent 70%)`,
      filter: "blur(22px)",
    }}
    animate={{ scale: [1, 1.1, 1], opacity: [0.7, 1, 0.7] }}
    transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut" }}
  />
);

/** Cycling caption + three breathing dots. */
const Caption = ({ messages, staticText }: { messages: string[]; staticText?: string }) => {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (staticText) return;
    const t = setInterval(() => setI((p) => (p + 1) % messages.length), 2200);
    return () => clearInterval(t);
  }, [messages.length, staticText]);

  return (
    <div className="mt-4 flex flex-col items-center">
      <div className="relative h-6 w-[240px]">
        {staticText ? (
          <p className="absolute inset-0 text-center text-[15px] font-medium text-[#475569]">{staticText}</p>
        ) : (
          <AnimatePresence mode="wait">
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.4 }}
              className="absolute inset-0 text-center text-[15px] font-medium text-[#475569]"
            >
              {messages[i]}
            </motion.p>
          </AnimatePresence>
        )}
      </div>
      <div className="mt-3 flex gap-1.5">
        {[0, 1, 2].map((d) => (
          <motion.span
            key={d}
            className="h-1.5 w-1.5 rounded-full"
            style={{ background: GRADIENT }}
            animate={{ opacity: [0.25, 1, 0.25], scale: [0.8, 1.15, 0.8] }}
            transition={{ duration: 1.2, repeat: Infinity, delay: d * 0.18, ease: "easeInOut" }}
          />
        ))}
      </div>
    </div>
  );
};

/** The one Genie, summoned from the smoke — fades/de-blurs in, then floats gently. */
const Mascot = ({ h }: { h: number }) => (
  <div className="relative flex items-end justify-center" style={{ height: h }}>
    <Halo size={h * 0.95} />
    <motion.img
      src={LOADING_GENIE}
      alt=""
      className="relative object-contain"
      style={{ height: h, filter: `drop-shadow(0 18px 34px ${BRAND}40)` }}
      initial={{ opacity: 0, scale: 0.92, filter: "blur(12px)" }}
      animate={{ opacity: 1, scale: 1, filter: "blur(0px)", y: [0, -12, 0], rotate: [-1.5, 1.5, -1.5] }}
      transition={{
        opacity: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
        scale: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
        filter: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
        default: { duration: 4, repeat: Infinity, ease: "easeInOut" },
      }}
    />
  </div>
);

/* ── component ──────────────────────────────────────────────────────────── */

export const GenieLoader = ({
  size = "block",
  loadingText,
  messages = DEFAULT_MESSAGES,
  className,
}: GenieLoaderProps) => {
  const h = HEIGHT[size];

  // Inline — a compact bobbing Genie, optionally with text beside it. No smoke.
  if (size === "inline") {
    return (
      <div className={cn("inline-flex items-center gap-3", className)}>
        <div className="relative flex items-end justify-center" style={{ height: h, width: h }}>
          <Halo size={h * 1.1} />
          <motion.img
            src={LOADING_GENIE}
            alt=""
            className="relative object-contain"
            style={{ height: h }}
            animate={{ y: [0, -4, 0] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
        {loadingText && <span className="text-sm font-medium text-[#475569]">{loadingText}</span>}
      </div>
    );
  }

  const stage = (
    <div className="relative flex flex-col items-center">
      <Mascot h={h} />
      <Caption messages={messages} staticText={loadingText} />
    </div>
  );

  if (size === "fullscreen") {
    return (
      <div className={cn("fixed inset-0 z-50 flex items-center justify-center bg-[#F9FAFC]", className)}>
        <div className="relative flex h-full w-full items-center justify-center overflow-hidden">
          <RisingSmoke scale={h / 280} />
          {stage}
        </div>
      </div>
    );
  }

  // block
  return (
    <div className={cn("relative flex w-full items-center justify-center overflow-hidden py-10", className)}>
      <RisingSmoke scale={h / 280} />
      {stage}
    </div>
  );
};

export default GenieLoader;
